/**
 * @file LocationsPanel.jsx
 * @brief Renders the location administration panel inside the catalog workspace.
 */
import styles from "../catalog-admin.module.css";
import { clampToMaxLength, FIELD_LIMITS } from "../../../constants/fieldLimits";
import useCatalogLocations from "../../../hooks/useCatalogLocations";
import { PaginationControls, SectionHeader } from "./shared";


/**
 * Render the CRUD interface for catalog locations.
 * @param {Object} props Component props.
 * @param {Object} props.api API client used for location operations.
 * @param {Function} props.t Translation helper.
 * @returns {JSX.Element} The locations admin panel.
 */
export default function LocationsPanel({ api, t }) {
    const {
        locations,
        count,
        page,
        next,
        previous,
        search,
        setSearch,
        name,
        setName,
        editId,
        setEditId,
        editName,
        setEditName,
        loading,
        saving,
        error,
        loadLocations,
        handleAdd,
        handleSave,
        handleDelete,
    } = useCatalogLocations({ api, t });

    return (
        <section className={styles.panel}>
            <SectionHeader
                icon="📍"
                title={t("catalogAdmin.locations")}
                subtitle={t("catalogAdmin.locationsSubtitle")}
                actions={
                    <button className={styles.ghostButton} onClick={() => loadLocations(page, search)} type="button">
                        {t("catalogAdmin.refresh")}
                    </button>
                }
            />

            <form className={styles.composerCompact} onSubmit={handleAdd}>
                <input
                    className={styles.input}
                    placeholder={t("catalogAdmin.newLocationPlaceholder")}
                    value={name}
                    onChange={(event) => setName(clampToMaxLength(event.target.value, FIELD_LIMITS.locationName))}
                    maxLength={FIELD_LIMITS.locationName}
                />
                <button className={styles.primaryButton} disabled={saving || !name.trim()} type="submit">
                    {saving ? t("catalogAdmin.saving") : t("catalogAdmin.addLocation")}
                </button>
            </form>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    placeholder={t("catalogAdmin.searchLocations")}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            loadLocations(1, event.currentTarget.value);
                        }
                    }}
                />
                <div className={styles.inlineToolbarGroup}>
                    <button className={styles.ghostButton} onClick={() => loadLocations(1, search)} type="button">
                        {t("catalogAdmin.search")}
                    </button>
                    <span className={styles.inlineMeta}>{t("catalogAdmin.resultCount", { count })}</span>
                </div>
            </div>

            {error && <p className={styles.errorMsg}>{error}</p>}

            {loading ? (
                <div className={styles.skeletonStack}>
                    {[...Array(5)].map((_, index) => (
                        <div key={index} className={styles.skeletonRow} />
                    ))}
                </div>
            ) : locations.length === 0 ? (
                <p className={styles.empty}>{t("catalogAdmin.noLocations")}</p>
            ) : (
                <ul className={styles.simpleList}>
                    {locations.map((location) => (
                        <li key={location.id} className={styles.simpleListRow}>
                            {editId === location.id ? (
                                <>
                                    <input
                                        className={styles.input}
                                        value={editName}
                                        onChange={(event) => setEditName(clampToMaxLength(event.target.value, FIELD_LIMITS.locationName))}
                                        autoFocus
                                        maxLength={FIELD_LIMITS.locationName}
                                    />
                                    <div className={styles.rowActions}>
                                        <button className={styles.successButton} disabled={saving} onClick={() => handleSave(location.id)} type="button">
                                            {t("catalogAdmin.save")}
                                        </button>
                                        <button className={styles.ghostButton} onClick={() => setEditId(null)} type="button">
                                            {t("catalogAdmin.cancel")}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <p className={styles.rowTitle}>{location.name}</p>
                                        <p className={styles.rowMeta}>{location.id}</p>
                                    </div>
                                    <div className={styles.rowActions}>
                                        <button
                                            className={styles.iconButton}
                                            onClick={() => {
                                                setEditId(location.id);
                                                setEditName(location.name);
                                            }}
                                            type="button"
                                        >
                                            {t("catalogAdmin.edit")}
                                        </button>
                                        <button className={styles.dangerButton} onClick={() => handleDelete(location.id)} type="button">
                                            {t("catalogAdmin.delete")}
                                        </button>
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <PaginationControls
                previous={previous}
                next={next}
                disabled={loading}
                onPrevious={() => loadLocations(Math.max(1, page - 1), search)}
                onNext={() => loadLocations(page + 1, search)}
                t={t}
            />
        </section>
    );
}
